import { useState, useEffect, ChangeEventHandler } from 'react';
import { IUser } from '../../../interfaces/user';
import { Button, Card, CardContent, IconButton, TextField, Typography } from '@mui/material';
import { GridColDef, GridAlignment } from '@mui/x-data-grid';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { MAX_PAGE_LIMIT } from '../../../constants/numbers';
import IResponse, { IPagination } from '../../../interfaces/response';
import BaranDataGrid from '../../common/BaranDataGrid';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Grid from '@mui/material/Unstable_Grid2/Grid2';
import { z } from 'zod';
import InformationCard from '../../common/InformationCard';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ForkLeftIcon from '@mui/icons-material/ForkLeft';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { LoadingButton } from '@mui/lab';
import BasicModal from '../../common/BasicModal.tsx';

interface IStats {
	total?: number;
	active?: number;
	inactive?: number;
	marketers?: number;
}

export default function AdminList() {
	const [pageSize, setPageSize] = useState(MAX_PAGE_LIMIT);
	const [page, setPage] = useState(1);
	const [name, setName] = useState('');
	const [lastName, setLastName] = useState('');
	const [email, setEmail] = useState('');
	const [emailError, setEmailError] = useState<string>();
	const [fetchedData, setFetchedData] = useState<IUser[]>([]);
	const [pagination, setPagination] = useState<IPagination | null>();
	const [stats, setStats] = useState<IStats>({});
	const [loading, setLoading] = useState(true);
	const [deleteLoading, setDeleteLoading] = useState(false);
	const [deleteUser, setDeleteUser] = useState<IUser>();
	const [refresh, setRefresh] = useState(1);
	const navigate = useNavigate();

	const headerClassNames = 'text-xs bg-background-paper';

	const { t } = useTranslation();

	const handleCreateNewPage = () => {
		navigate('/admin/admins/new');
	};

	const handleDelete = (value: IUser) => () => {
		setDeleteUser(value);
	};

	const columns: GridColDef<IUser>[] = [
		{
			field: 'name',
			headerName: t('first_name_and_last_name'),
			headerAlign: 'left' as GridAlignment,
			align: 'left' as GridAlignment,
			headerClassName: headerClassNames,
			flex: 1,
		},
		{
			field: 'email',
			headerName: t('email'),
			headerAlign: 'left' as GridAlignment,
			align: 'left' as GridAlignment,
			headerClassName: headerClassNames,
			flex: 1,
		},
		{
			field: 'created_at',
			headerName: t('date'),
			width: 100,
			headerAlign: 'left' as GridAlignment,
			align: 'left' as GridAlignment,
			headerClassName: headerClassNames,
			renderCell: params => {
				return new Date(params.value).toLocaleDateString('fa-IR');
			},
		},
		{
			field: 'status',
			headerName: t('status'),
			width: 150,
			headerAlign: 'left' as GridAlignment,
			align: 'left' as GridAlignment,
			headerClassName: `${headerClassNames} ps-12`,
			renderCell: params => {
				const statusText = params.value ? t('confirmed') : t('unconfirmed');
				const bgColorClass = params.value ? 'bg-success-selected text-success-main' : 'bg-error-selected text-error-main';
				return <div className={`ms-10 mt-4 flex w-[max-content] items-center rounded px-3 py-0.5 text-xs font-medium ${bgColorClass}`}>{statusText}</div>;
			},
		},
		{
			field: 'action',
			headerName: t('action'),
			minWidth: 200,
			headerAlign: 'center' as GridAlignment,
			align: 'center' as GridAlignment,
			headerClassName: headerClassNames,
			renderCell: params => {
				return (
					<>
						<IconButton component={Link} to={`/admin/admin/${params.row.id}/edit`}>
							<EditIcon className="text-text-secondary" />
						</IconButton>
						<IconButton onClick={handleDelete(params.row)}>
							<DeleteOutlineIcon className="text-text-secondary" />
						</IconButton>
					</>
				);
			},
		},
	];

	useEffect(() => {
		setLoading(true);
		axios<IResponse<IUser[]>>({
			url: `/admin/admins/`,
			params: {
				page: page > 0 ? page : 1,
				limit: pageSize,
				first_name: name,
				last_name: lastName,
				email: z.string().email().safeParse(email).success ? email : undefined,
			},
		})
			.then(res => {
				if (res.data.success) {
					setPagination(res.data.pagination);
					setFetchedData(res.data.data);
				}
			})
			.finally(() => {
				setLoading(false);
			});
	}, [page, pageSize, refresh]);

	useEffect(() => {
		axios<IResponse<IStats>>({
			url: '/admin/admins/stats',
		}).then(res => {
			if (res.data.success) {
				setStats(res.data.data);
			}
		});
	}, []);

	useEffect(() => {
		const id = setTimeout(() => {
			if (page === 1) {
				setPage(0);
			} else {
				setPage(1);
			}
		}, 500);
		return () => clearTimeout(id);
	}, [name, lastName, email]);

	const handleChangeName: ChangeEventHandler<HTMLInputElement> = e => {
		setName(e.target.value);
	};

	const handleChangeLastName: ChangeEventHandler<HTMLInputElement> = e => {
		setLastName(e.target.value);
	};

	const handleChangeEmail: ChangeEventHandler<HTMLInputElement> = e => {
		if (e.target.value.length === 0 || z.string().email().safeParse(e.target.value).success) {
			setEmailError(undefined);
			setEmail(e.target.value);
		} else {
			setEmailError(t('invalid_email'));
		}
	};

	const handleDeleteAdmin = () => {
		setDeleteLoading(true);
		axios<IResponse<any>>({
			url: `/admin/admins/delete/${deleteUser?.id}`,
			method: 'DELETE',
		})
			.then(res => {
				if (res.data.success) {
					setDeleteUser(undefined);
					setRefresh(prev => prev + 1);
				}
			})
			.finally(() => {
				setDeleteLoading(false);
			});
	};

	const handleCloseDeleteDialog = () => {
		setDeleteUser(undefined);
	};

	//TODO must handle user access level for filtering

	return (
		<>
			<BasicModal
				open={!!deleteUser}
				onClose={handleCloseDeleteDialog}
				title={t('delete_role')}
				titleClassName={'text-center grow '}
				subClassName={'flex flex-row-reverse'}
				className={'max-w-sm rounded-lg'}
				showBackIcon={false}
				action={
					<>
						<Button onClick={handleCloseDeleteDialog} color="inherit">
							{t('cancel')}
						</Button>
						<LoadingButton loading={deleteLoading} onClick={handleDeleteAdmin} variant="outlined" color="error">
							{t('delete')}
						</LoadingButton>
					</>
				}
			>
				<Typography>{t('delete_admin_description', { admin: deleteUser?.name })}</Typography>
			</BasicModal>

			<div className={'w-full'}>
				<Grid container rowSpacing={2} columnSpacing={3}>
					<Grid xs={6} md={3}>
						<InformationCard title={t('all_users')} icon={<AccessTimeIcon />} color="green" description={stats.total?.toLocaleString()} />
					</Grid>
					<Grid xs={6} md={3}>
						<InformationCard title={t('active_users')} icon={<ForkLeftIcon />} color="secondary" description={stats.active?.toLocaleString()} />
					</Grid>
					<Grid xs={6} md={3}>
						<InformationCard title={t('inactive_users')} icon={<WarningAmberIcon />} color="orange" description={stats.inactive?.toLocaleString()} />
					</Grid>
					<Grid xs={6} md={3}>
						<InformationCard title={t('marketer_users')} icon={<LocalShippingIcon />} color="primary" description={stats.marketers?.toLocaleString()} />
					</Grid>
				</Grid>
				<Card className="my-3">
					<CardContent>
						<Typography className="mb-4 text-lg">{t('filter')}</Typography>
						<Grid container rowSpacing={2} columnSpacing={3}>
							<Grid xs={6} md={4}>
								<TextField onChange={handleChangeName} fullWidth label={t('name')} />
							</Grid>
							<Grid xs={6} md={4}>
								<TextField onChange={handleChangeLastName} fullWidth label={t('last_name')} />
							</Grid>
							<Grid xs={6} md={4}>
								<TextField onChange={handleChangeEmail} fullWidth label={t('email')} error={!!emailError} helperText={emailError} />
							</Grid>
							<Grid xs={6} md={4}>
								<TextField fullWidth label={t('access_level')} />
							</Grid>
						</Grid>
					</CardContent>
				</Card>
				<BaranDataGrid<IUser> checkboxSelection={true} loading={loading} pagination={pagination} rows={fetchedData} columns={columns} pageSize={pageSize} setPageSize={setPageSize} page={page > 0 ? page : 1} setPage={setPage} onClick={handleCreateNewPage} actionButtonLable={t('add_new_user')} />
			</div>
		</>
	);
}
